import { Component } from '@angular/core';
import { Input } from '@angular/core';

import { viewService } from '@unpispas/upp-base';

/**
 * A reusable header component for panels in an Ionic application.
 *
 * The `UppHeaderComponent` is designed to serve as a customizable header
 * with support for a menu button, dynamic content, and action buttons.
 *
 * @example
 * <upp-header [menu]="'show'">
 *   <upp-header-content>
 *     <ion-label>Main Header</ion-label>
 *   </upp-header-content>
 *   <upp-header-buttons>
 *     <ion-button (click)="onEdit()">Edit</ion-button>
 *     <ion-button (click)="onDelete()">Delete</ion-button>
 *   </upp-header-buttons>
 * </upp-header>
 *
 * @see {@link UppHeaderContentComponent}
 * @see {@link UppHeaderButtonsComponent}
 */
@Component({
    selector: 'upp-header',
    templateUrl: './upp-header.html',
    styleUrls: ['./upp-header.scss'],
})
export class UppHeaderComponent {
    /**
     * Determines whether the menu button should be displayed.
     *
     * - `'show'`: The menu button is visible if the view is in mobile mode.
     * - `'hide'`: The menu button is always hidden.
     *
     * @default `'show'`
     */    
    @Input() menu: 'show' | 'hide' = 'show';

    /**
     * Determines the header background color (primary, success, warning..).
     * @default `'warning'`
     */    
    @Input() color = 'warning'; 

    /**
     * Determines the header height.
     * @default `50px`
     */    
    @Input() height = '50px'; 

    /**
     * Determines the color of the bottom border (primary, success, warning..).
     * 
     * null for no border at all
     * 
     * @default `'null'`
     */        
    @Input() lines: string | null = null; 

    /**
     * Indicates whether the menu button should be displayed based on the `menu` input
     * and the current view's mobile status.
     *
     * @returns `true` if the menu is set to `'show'` and the view is mobile, otherwise `false`.
     */    
    public get ShowMenu(){
        return (this.menu == 'show') && this.view.Mobile;
    }

    /**
     * Creates an instance of `UppHeaderComponent`.
     *
     * @param view - The service that provides information about the current view (e.g., mobile or desktop).
     */  
    constructor(public view: viewService){
        // nothing to do
    }
}

/**
 * A component for defining the title within a header.
 *
 * The `UppHeaderTitleComponent` serves as a wrapper for the header's title content,
 * allowing the consumer to insert dynamic or static content.
 *
 * @example
 * <upp-header>
 *   <upp-header-title>
 *     Main Title
 *   </upp-header-title>
 * </upp-header>
 */
@Component({
    selector: 'upp-header-title',
    template: `
        <ion-label>
            <ng-content></ng-content>
        </ion-label>
    `,
    styleUrls: ['./upp-header.scss']
})
export class UppHeaderTitleComponent {
    // nothing to do
}

/**
 * A component for adding buttons to a header.
 *
 * The `UppHeaderButtonComponent` acts as a wrapper for buttons, encapsulating
 * them within a standard `<ion-button>` to ensure a consistent style.
 *
 * @example
 * <upp-header>
 *   <upp-header-button>
 *     <ion-icon name="add"></ion-icon>
 *   </upp-header-button>
 * </upp-header>
 */
@Component({
    selector: 'upp-header-button',
    template: `
        <ion-button [ngClass]="size">
            <ng-content></ng-content>
        </ion-button>
    `,
    styleUrls: ['./upp-header.scss']
})
export class UppHeaderButtonComponent {
    @Input() size: 'large' | 'small' = 'large';
}

/**
 * A content container for the header component.
 *
 * Use `UppHeaderContentComponent` to define the main content of the header,
 * such as the title or other information.
 *
 * @example
 * <upp-header>
 *   <div upp-header-content>
 *     <ion-label>Main Header</ion-label>
 *   </div>
 * </upp-header>
 *
 * @see {@link UppHeaderComponent}
 */
@Component({
    selector: 'upp-header-content',
    template: `<ng-content></ng-content>`,
    styleUrls: [],
})
export class UppHeaderContentComponent {
    // nothing to do
}
